import interpolate from 'lib/interpolate';
import invariant from 'fbjs/lib/invariant.js';
import { RequestError } from '#/lib/errors/errors.js';
import { rewireConfig } from '#/services/rewire-config.js';

const TRY_COUNT = Symbol('tryCount');
const MAX_RETRY_COUNT = 3;

/**
 * @class InteractiveAPI
 * @description Class for fetching data from the API
 * @param {Object} configProvider - observable config holder
 * @param {(error) => {handled: boolean, retry: boolean, queue: boolean}} errorHnadler - callbacks for handling errors
 */
export default class InteractiveApi {
  /**
   * Keep all pending queries
   * @type {Request[]}
   */
  #pendingQuery = [];
  /**
   * @type {(error: RequestError) => {handled: boolean, retry: boolean, queue: boolean}}
   */

  /**
   * Keep interactive API token
   * @type {string}
   */
  #token;
  #baseUrl;
  #videoConfigTpl;
  #configVersion;

  #recoveries = [
/*
    (response) => {
      console.warn('Fake recovery, just for dev');
      if (response.status === 400) {
        return {
          delay: 1_000,
        };
      }
    },
*/
    (response) => {
      if (response.status === 429 || response.status >= 500 /* this includes custom, 9xx user agent, errors */) {
        return {
          delay: this.#getRetryDelay(response),
        };
      }
    },
  ];

  constructor(configProvider, errorHandler) {
    invariant(configProvider, 'configProvider is required');
    this.#recoveries.push(errorHandler);

    configProvider.subscribe(async (config) => {
      this.#token = config.token;
      this.#coreAccessToken = null; // reset core token on token change as it may be invalid
      this.#baseUrl = config.api_config.url;
      this.#videoConfigTpl = config.api_config.video_config_url;
      this.#configVersion = config.api_config.version;

      this.#handleQueue();
    });
  }

  #getConfigUrl(object) {
    invariant(
      object?.video_id,
      'Get config failure: Missing required property `video_id`',
    );
    invariant(
      object?.content_type,
      'Get config failure: Missing required property `content_type` for video_id: %s',
      object.video_id,
    );

    return interpolate(this.#videoConfigTpl, object);
  }

  // testBrokenToken = false;
  get #Authorization() {
    /*
    if (this.testBrokenToken) {
      this.testBrokenToken = false;
      return `Bearer broken_${this.#token}`;
    } else {
      this.testBrokenToken = true;
      return `Bearer ${this.#token}`;
    }
*/

    return `Bearer ${this.#token}`;
  }

  query(path) {
    const request =
      path instanceof Request ? path : new Request(this.#baseUrl + path);

    request[TRY_COUNT] ??= 0;

    if (request[TRY_COUNT] >= MAX_RETRY_COUNT) {
      return Promise.reject(
        new RequestError({
          status: 901,
          statusText: 'API request failure',
          url: request.url,
          detail: 'Max retry count exceeded',
        }),
      );
    }

    request.headers.set('Authorization', this.#Authorization);
    request.headers.set('Accept', 'application/json');

/*
    console.debug(
      'Query:',
      request.url,
      request[TRY_COUNT],
      request.headers.get('Accept'),
    );
*/

    request[TRY_COUNT]++;

    return fetch(request)
      .then(this.#parseResponse, () => this.#onRejectedRequest(request.url))
      .then((payload) => this.#checkResponsePayload(payload))
      .catch((error) => {
        for (const element of this.#recoveries) {
          const recovery = element(error);
          if (!recovery) {
            continue;
          }
          if (recovery.delay) {
            console.debug(`Attempt to recovery with delay`);
            return new Promise((resolve) => {
              setTimeout(() => {
                console.debug('Recovery attempt');
                resolve(this.query(request));
              }, recovery.delay);
            });
          } else {
            console.debug(`Attempt to recovery on token update`);
            return new Promise((resolve) => {
              this.#pendingQuery.push(() => {
                console.log('Recovery attempt');
                resolve(this.query(request));
              });
            });
          }
        }

        error.url ??= request.url;
        console.debug('No way to recovery', error);
        throw error;
      });
  }

  post(path, body, contentType = 'json') {
    const request = new Request(this.#baseUrl + path, { method: 'POST', body });

    request.headers.set('Content-type', `application/${contentType}`);

    return this.query(request);
  }

  get userStatus() {
    try {
      const userInfo = JSON.parse(atob(this.#token.split('.')[1]));
      return userInfo.user_status;
    } catch {
      return null;
    }
  }

  #coreAccessToken;

  async #getCoreToken() {
    invariant(this.userStatus && this.userStatus !== 'anon', 'Anonymous user is not allowed');

    if (!this.#coreAccessToken) {
      this.#coreAccessToken = this.post(`/core/token/${this.#token}`)
          .then((data) => data.tokens.access_token)
          .catch((error) => {
            // On error, we should invalidate core token and re-throw error.
            // Empty token will be used on next request to obtain new token.
            // Re-throw error prevent consume empty token.
            this.#coreAccessToken = null;
            throw error;
          });
    }
    return this.#coreAccessToken;
  }

  /**
   *
   * @param {function} executor
   * @return {function(*, *): Promise<*>}
   */
  #jAPI(executor) {
    const _callExecutor = async (path, payload, callCounter = 1) => {
      const accessToken = await this.#getCoreToken();

      const search = new URLSearchParams({
        access_token: accessToken,
        ...payload,
      });

      /**
       * Unconditionally remove that parameter, our gate add it automatically.
       * We should never ever pass it from client due to it leads to crash proxying query.
       */
      search.delete('user_profile_id');

      return executor(`/japi-core/proxy/${path}`, search)
        .catch((error) => {
          /* We should handle jAPI requests errors here due to standard recovery does not work
        AND we must rebuild path/payload on changing jAPI token */
          if (callCounter >= MAX_RETRY_COUNT) {
            error.detail = 'Max retry count exceeded';
            throw error;
          }
          if (error.status === 403) {
            /* When we have such error we should invalidate jAPI access token and try to obtain new */
            this.#coreAccessToken = null;
            return _callExecutor(path, payload, callCounter + 1);
          }
          throw error;
        });
    }

    return async (path, payload) => {
      return _callExecutor(path, payload);
    }
  }

  getTrackerData(profileId){
    invariant(
      profileId,
      'Get tracker data failure: Missing required property `profileId`',
    );
    return this.post(`/tracker/data/${profileId}`)
      .then((data) => {
        console.debug('tracker data fetched', data);
        return data;
      })
      .catch((error) => {
        console.warn('tracker data fetch error', error);
        /*
          It is necessary to remap 422 error to 404, to prevent creative loading retry
         */
        if (error.status === 422) {
          const err = new Error('Tracker data not found');
          err.status = 404;
          throw err;
        }
        throw error;
      });
  }
  /**
   * Make query to jAPI
   * @param {string} path
   * @param {object} [params={}]
   * @return {Promise<unknown>}
   */
  japiQuery(path, params = {}) {
    invariant(path, 'Path is required');
    return this.#jAPI((...parts) => this.query(parts.join('?')))(path, params);
  }

  japiPost(path, payload = {}) {
    invariant(path, 'Path is required');
    return this.#jAPI((...parts) => this.post(...parts, 'x-www-form-urlencoded'))(path, payload);
  }

  /**
   * Request EPG time from jAPI, in case of failure return current timestamp in seconds.
   * Case for failure: site's "anon" user.
   * @return {Promise<{timestamp: number}>}
   */
  async getEPGTime(){
    const path = 'epg/time';
    return this.japiQuery(path)
      .catch(() => {
        return { timestamp: Math.floor(Date.now() / 1_000) }
      })
  }

  init() {
    console.warn('Unexpected usage');
    return this.post('/v1/init');
  }

  async getConfig(object) {
    invariant(
      object?.video_id,
      'Get config failure: Missing required property `video_id`',
    );
    invariant(
      object?.content_type,
      'Get config failure: Missing required property `content_type` for video_id: %s',
      object.video_id,
    );

    const url = this.#getConfigUrl(object);
    return this.query(url)
      .then((config) => {
        config._version = this.#configVersion;
        config.server_timestamp ??= config._server_timestamp;

        // TODO: remove after BE updates config for quiz
        config.quiz ??= {
          enabled: true,
        };
        // TODO: remove manual patching
        config['debug-layer'] ??= {
          enabled: true,
        };

        console.debug('config fetched', {
          object,
          config,
        });

        config.emoji ??= {};
        config.emoji.server_timestamp ??= config.server_timestamp;

        // TODO: move here all other config "modifications" from fetchObjectConfig,
        //  except replacing video_id in templates
        return config;
      })
      .then((config) => rewireConfig(config, object))
      .then((config) => {
        // rename backend preferred name to required "as same as extension" name

        config['promo-landing'] = config['promo'];
        delete config['promo']; // ????

        // config['drop-notification'] = config['gifts'];
        delete config['gifts'];

        if (config._version === '3' && config.poll?.enabled) {
          console.warn("apply poll's config patch");
          /* patch v3 config to match v4,
           * delete after transferring all clients to version 4 */
          config.poll.subscribe = {
            event: '/v1/video/{{video_id}}/device/{{device_id}}',
            result: config['poll'].subscribe,
          };
          config.poll.status_api = '/v3/poll/{{poll_id}}/voted';
        }

        return config;
      });
  }

  #handleQueue() {
    this.#pendingQuery.forEach((callback) => callback());
    this.#pendingQuery.length = 0;
  }

  #getRetryDelay(response) {
    const retryHeader = response.headers?.get('Retry-After');
    if (retryHeader && typeof retryHeader === 'number') {
      return retryHeader * 1_000;
    }
    return 5_000;
  }

  #parseResponse(response) {
    if (response.ok) {
      return response.json()
        .then((payload) => {
          try {
            payload._server_timestamp = Date.parse(response.headers.get('Date')) / 1_000;
          } catch {
            payload._server_timestamp = Date.now() / 1_000;
          }
          return payload;
        });
    }

    console.warn('Catch error in response status');
    throw new RequestError(response);
  }

  #checkResponsePayload(payload) {
    const { result, data  = {}} = payload;

    if (result === 'ok') {
      data._server_timestamp ??= payload._server_timestamp;
      return data;
    }

    console.warn('Catch error in response data');
    const { message: statusText, code: status } = payload;
    throw new RequestError({ status, statusText });
  }

  #onRejectedRequest(url) {
    console.warn('Catch error on request attempt');
    throw new RequestError({
      status: 900,
      statusText: 'API request failure',
      url,
    });
  }
}
